import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  changePassword,
  getMe,
  getStoredUser,
  setupTwoFa,
  verifyTwoFa,
} from "../../auth/services/authService";
import { setSession } from "../../auth/store/authSlice";
import { getUserByEmail, getUsers, updateUser } from "../../users/services/usersService";

const VALID_TIMEZONES = ["PKT", "GMT", "UTC", "EST", "CST", "MST", "PST"];
const TIMEZONE_OPTIONS = ["UTC", "GMT", "EST", "CST", "MST", "PST", "PKT"];

const getSafeTimezone = (timezone) =>
  VALID_TIMEZONES.includes(timezone) ? timezone : "UTC";

const getDisplayRole = (user) => {
  const backendRole = (user.user_role || "").toString().trim().toLowerCase();
  if (backendRole === "admin") return "Admin";
  if (backendRole === "owner") return "Owner";
  if (backendRole === "manager") return "Manager";
  if (backendRole === "viewer") return "Viewer";
  return "Viewer";
};

const normalizeRoleForApi = (role) => {
  const selectedRole = (role || "").toString().trim().toLowerCase();
  if (selectedRole === "owner") return "owner";
  if (selectedRole === "manager") return "manager";
  if (selectedRole === "admin") return "admin";
  return "viewer";
};

const toAdminUser = (user) => ({
  id: user.id,
  name: [user.firstname, user.lastname].filter(Boolean).join(" ") || user.email,
  email: user.email || "",
  role: getDisplayRole(user),
  backendRole: user.user_role || "",
  mfaEnabled: Boolean(user.two_fa_enabled),
  avatar: null,
});

const getUsersList = (response) => {
  if (Array.isArray(response?.users)) return response.users;
  if (Array.isArray(response)) return response;
  return [];
};

const getUserFromLookup = (response) => {
  if (response?.id) return response;
  if (response?.user?.id) return response.user;
  if (Array.isArray(response?.users) && response.users[0]?.id) return response.users[0];
  if (Array.isArray(response) && response[0]?.id) return response[0];
  return null;
};

const buildUserPayload = (user, role) => ({
  firstname: user.firstname || "",
  lastname: user.lastname || "",
  phone: user.phone || undefined,
  sms_notification: Boolean(user.sms_notification),
  timezone: getSafeTimezone(user.timezone),
  user_status: user.user_status || "enabled",
  two_fa_enabled: Boolean(user.two_fa_enabled),
  user_role: normalizeRoleForApi(role),
});

const buildAccountPayload = (user, { timezone, twoFaEnabled }) => {
  const payload = {
    firstname: user.firstname || "",
    lastname: user.lastname || "",
    phone: user.phone || undefined,
    sms_notification: Boolean(user.sms_notification),
    timezone: getSafeTimezone(timezone || user.timezone),
    user_status: user.user_status || "enabled",
    two_fa_enabled: Boolean(twoFaEnabled),
  };

  if (!user.user_type || user.user_type === "console") {
    payload.user_role = normalizeRoleForApi(user.user_role || "viewer");
  }

  return payload;
};

const mergeSessionUser = (nextUser, previousUser) => ({
  ...previousUser,
  ...nextUser,
  client_id:
    nextUser?.client_id ??
    nextUser?.clientId ??
    nextUser?.client?.id ??
    nextUser?.client?.client_id ??
    previousUser?.client_id ??
    previousUser?.clientId ??
    previousUser?.client?.id ??
    previousUser?.client?.client_id ??
    null,
  client_name:
    nextUser?.client_name ??
    nextUser?.clientName ??
    nextUser?.client?.client_name ??
    nextUser?.client?.name ??
    previousUser?.client_name ??
    previousUser?.clientName ??
    previousUser?.client?.client_name ??
    previousUser?.client?.name ??
    "",
});

export const fetchAdminUsersThunk = createAsyncThunk(
  "admin/fetchUsers",
  async (_, { getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    const response = await getUsers(clientId, { limit: 100, offset: 0 });
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load admin users.");
    }
    return getUsersList(response);
  },
);

export const inviteAdminThunk = createAsyncThunk(
  "admin/inviteAdmin",
  async ({ email, role }, { dispatch, getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    const lookupResponse = await getUserByEmail(email.trim());
    if (lookupResponse?.error) {
      return rejectWithValue(lookupResponse.error.message);
    }

    const existingUser = getUserFromLookup(lookupResponse);
    if (!existingUser) {
      return rejectWithValue("No existing user was found for that email address.");
    }

    if (existingUser.client_id && String(existingUser.client_id) !== String(clientId)) {
      return rejectWithValue("That user belongs to a different organization.");
    }

    const updateResponse = await updateUser(
      existingUser.id,
      buildUserPayload(existingUser, role),
    );

    if (updateResponse?.error) {
      return rejectWithValue(updateResponse.error.message);
    }

    await dispatch(fetchAdminUsersThunk());
    return "Admin access updated successfully.";
  },
);

export const saveAdminThunk = createAsyncThunk(
  "admin/saveAdmin",
  async ({ updatedUser, users }, { dispatch, rejectWithValue }) => {
    const existingUser = users.find((user) => user.id === updatedUser.id);
    if (!existingUser) {
      return rejectWithValue("Unable to find the selected user.");
    }

    const existingEmail = String(existingUser.email || "").trim().toLowerCase();
    const requestedEmail = String(updatedUser.email || "").trim().toLowerCase();
    if (existingEmail !== requestedEmail) {
      return rejectWithValue(
        "Email updates are not supported by the current API endpoint. Update role only.",
      );
    }

    const response = await updateUser(
      updatedUser.id,
      buildUserPayload(existingUser, updatedUser.role),
    );

    if (response?.error) {
      return rejectWithValue(response.error.message);
    }

    await dispatch(fetchAdminUsersThunk());
    return "Admin user updated successfully.";
  },
);

export const revokeAdminThunk = createAsyncThunk(
  "admin/revokeAdmin",
  async ({ user, users }, { dispatch, rejectWithValue }) => {
    const existingUser = users.find((item) => item.id === user.id);
    if (!existingUser) {
      return rejectWithValue("Unable to find the selected user.");
    }

    const response = await updateUser(
      user.id,
      buildUserPayload(existingUser, "Viewer"),
    );

    if (response?.error) {
      return rejectWithValue(response.error.message);
    }

    await dispatch(fetchAdminUsersThunk());
    return "Admin access revoked successfully.";
  },
);

export const fetchAccountSettingsUserThunk = createAsyncThunk(
  "admin/fetchAccountSettingsUser",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const response = await getMe();

    if (response?.error) {
      const storedUser = getStoredUser();
      if (storedUser) {
        return {
          user: storedUser,
          loadError: "",
        };
      }

      return rejectWithValue(response.error.message || "Unable to load account details.");
    }

    const existingSession = getState()?.auth?.session || {};
    dispatch(
      setSession({
        token: existingSession?.token || null,
        user: mergeSessionUser(response, existingSession?.user),
        isAuthenticated: Boolean(existingSession?.token),
      }),
    );

    return {
      user: response,
      loadError: "",
    };
  },
);

export const setupAccountMfaThunk = createAsyncThunk(
  "admin/setupAccountMfa",
  async (email, { rejectWithValue }) => {
    if (!email) {
      return rejectWithValue("Unable to set up MFA because email is missing.");
    }

    const response = await setupTwoFa({ email });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to set up MFA.");
    }

    const resolvedSecret = String(
      response?.two_fa?.secret || response?.secret || "",
    ).trim();
    const resolvedQrCodeUrl = String(
      response?.two_fa?.qrCodeUrl || response?.qrCodeUrl || "",
    ).trim();

    if (!resolvedSecret || !resolvedQrCodeUrl) {
      return rejectWithValue("MFA setup details were not returned by the server.");
    }

    return {
      secret: resolvedSecret,
      qrCodeUrl: resolvedQrCodeUrl,
    };
  },
);

export const verifyAccountMfaThunk = createAsyncThunk(
  "admin/verifyAccountMfa",
  async ({ email, token, secret }, { rejectWithValue }) => {
    if (!email) {
      return rejectWithValue("Unable to verify MFA because email is missing.");
    }

    if (!token) {
      return rejectWithValue("Please enter the 6-digit authenticator code.");
    }

    if (!secret) {
      return rejectWithValue("MFA setup secret is missing. Please restart MFA setup.");
    }

    const response = await verifyTwoFa({ email, token, secret });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to verify MFA code.");
    }

    const isVerified = Boolean(
      response?.two_fa?.result ?? response?.result ?? response?.two_fa,
    );

    if (!isVerified) {
      return rejectWithValue("Invalid authenticator code. Please try again.");
    }

    return true;
  },
);

export const saveAccountSettingsThunk = createAsyncThunk(
  "admin/saveAccountSettings",
  async ({
    user,
    timezone,
    mfaEnabled,
    oldPassword,
    newPassword,
    confirmPassword,
    mfaCodeVerified,
  }, { dispatch, getState, rejectWithValue }) => {
    if (!user) {
      return rejectWithValue("Unable to find account details for this user.");
    }

    const hasPasswordChange = oldPassword || newPassword || confirmPassword;

    if (hasPasswordChange) {
      if (!oldPassword || !newPassword || !confirmPassword) {
        return rejectWithValue("Please fill all password fields to change password.");
      }

      if (newPassword !== confirmPassword) {
        return rejectWithValue("New passwords do not match.");
      }

      if (newPassword.length < 8) {
        return rejectWithValue("Password must be at least 8 characters long.");
      }

      const passwordResponse = await changePassword({
        email: user.email || "",
        password: oldPassword,
        new_password: newPassword,
      });

      if (passwordResponse?.error) {
        return rejectWithValue(passwordResponse.error.message || "Unable to change password.");
      }
    }

    const isMfaChanged = Boolean(mfaEnabled) !== Boolean(user.two_fa_enabled);
    const isTimezoneChanged = getSafeTimezone(timezone) !== getSafeTimezone(user.timezone);

    if (isMfaChanged && mfaEnabled && !mfaCodeVerified) {
      return rejectWithValue("Please verify the 6-digit MFA code before saving changes.");
    }

    if (isMfaChanged || isTimezoneChanged) {
      if (!user.id) {
        return rejectWithValue(
          "Unable to update account details because user information is unavailable.",
        );
      }

      const updateResponse = await updateUser(
        user.id,
        buildAccountPayload(user, {
          timezone,
          twoFaEnabled: mfaEnabled,
        }),
      );

      if (updateResponse?.error) {
        return rejectWithValue(updateResponse.error.message || "Unable to save account settings.");
      }
    }

    const existingSession = getState()?.auth?.session || {};
    const nextUser = mergeSessionUser({
      ...user,
      timezone: getSafeTimezone(timezone),
      two_fa_enabled: Boolean(mfaEnabled),
    }, existingSession?.user);

    dispatch(
      setSession({
        token: existingSession?.token || null,
        user: nextUser,
        isAuthenticated: Boolean(existingSession?.token),
      }),
    );

    return {
      user: nextUser,
      successMessage: "Changes saved successfully.",
      clearPasswordFields: hasPasswordChange,
    };
  },
);

const initialState = {
  users: [],
  isLoading: false,
  isSaving: false,
  error: "",
  successMessage: "",
  hasLoaded: false,
  accountUser: null,
  accountLoadError: "",
  isAccountLoading: false,
  isAccountSaving: false,
  isSettingUpMfa: false,
  isVerifyingMfa: false,
  accountError: "",
  accountSuccessMessage: "",
  mfaSetup: {
    secret: "",
    qrCodeUrl: "",
  },
  timezoneOptions: TIMEZONE_OPTIONS,
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearAdminMessages: (state) => {
      state.error = "";
      state.successMessage = "";
    },
    clearAccountMessages: (state) => {
      state.accountError = "";
      state.accountSuccessMessage = "";
    },
    clearAccountMfaSetup: (state) => {
      state.mfaSetup = {
        secret: "",
        qrCodeUrl: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminUsersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
      .addCase(fetchAdminUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
        state.hasLoaded = true;
      })
      .addCase(fetchAdminUsersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Unable to load admin users.";
        state.users = [];
        state.hasLoaded = true;
      })
      .addCase(inviteAdminThunk.pending, (state) => {
        state.isSaving = true;
        state.error = "";
        state.successMessage = "";
      })
      .addCase(inviteAdminThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successMessage = action.payload;
      })
      .addCase(inviteAdminThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Unable to update admin access.";
      })
      .addCase(saveAdminThunk.pending, (state) => {
        state.isSaving = true;
        state.error = "";
        state.successMessage = "";
      })
      .addCase(saveAdminThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successMessage = action.payload;
      })
      .addCase(saveAdminThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Unable to update admin user.";
      })
      .addCase(revokeAdminThunk.pending, (state) => {
        state.isSaving = true;
        state.error = "";
        state.successMessage = "";
      })
      .addCase(revokeAdminThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successMessage = action.payload;
      })
      .addCase(revokeAdminThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Unable to revoke admin access.";
      })
      .addCase(fetchAccountSettingsUserThunk.pending, (state) => {
        state.isAccountLoading = true;
        state.accountLoadError = "";
      })
      .addCase(fetchAccountSettingsUserThunk.fulfilled, (state, action) => {
        state.isAccountLoading = false;
        state.accountUser = action.payload.user;
        state.accountLoadError = action.payload.loadError || "";
        state.mfaSetup = {
          secret: "",
          qrCodeUrl: "",
        };
      })
      .addCase(fetchAccountSettingsUserThunk.rejected, (state, action) => {
        state.isAccountLoading = false;
        state.accountLoadError = action.payload || "Unable to load account details.";
        state.accountUser = null;
      })
      .addCase(setupAccountMfaThunk.pending, (state) => {
        state.isSettingUpMfa = true;
        state.accountError = "";
      })
      .addCase(setupAccountMfaThunk.fulfilled, (state, action) => {
        state.isSettingUpMfa = false;
        state.mfaSetup = action.payload;
        state.accountSuccessMessage = "Authenticator setup started. Scan the QR code to continue.";
      })
      .addCase(setupAccountMfaThunk.rejected, (state, action) => {
        state.isSettingUpMfa = false;
        state.accountError = action.payload || "Unable to set up MFA.";
      })
      .addCase(verifyAccountMfaThunk.pending, (state) => {
        state.isVerifyingMfa = true;
        state.accountError = "";
      })
      .addCase(verifyAccountMfaThunk.fulfilled, (state) => {
        state.isVerifyingMfa = false;
        state.accountSuccessMessage = "Authenticator verified successfully.";
      })
      .addCase(verifyAccountMfaThunk.rejected, (state, action) => {
        state.isVerifyingMfa = false;
        state.accountError = action.payload || "Unable to verify authenticator code.";
      })
      .addCase(saveAccountSettingsThunk.pending, (state) => {
        state.isAccountSaving = true;
        state.accountError = "";
        state.accountSuccessMessage = "";
      })
      .addCase(saveAccountSettingsThunk.fulfilled, (state, action) => {
        state.isAccountSaving = false;
        state.accountUser = action.payload.user;
        state.accountSuccessMessage = action.payload.successMessage;
        state.mfaSetup = {
          secret: "",
          qrCodeUrl: "",
        };
      })
      .addCase(saveAccountSettingsThunk.rejected, (state, action) => {
        state.isAccountSaving = false;
        state.accountError = action.payload || "Failed to save changes.";
      });
  },
});

export const {
  clearAccountMessages,
  clearAccountMfaSetup,
  clearAdminMessages,
} = adminSlice.actions;
export const selectAdminState = (state) => state.admin;
const selectAdminUsersSource = (state) => state.admin.users;

export const selectAdminUsers = createSelector(
  [selectAdminUsersSource],
  (users = []) =>
    users
      .filter((user) => {
        const role = (user.user_role || "").toString().trim().toLowerCase();
        return ["admin", "owner", "manager", "viewer"].includes(role);
      })
      .map(toAdminUser),
);

export default adminSlice.reducer;
